import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import apiConfig from '../config/apiConfig.js';

// Gift Flow Context Types
interface GiftData {
  // Step 1: Gift Information
  _id?: string;
  title: string;
  recipientId: string;
  type: 'Regular' | 'Post-Mortem';
  occasionType: 'Fixed' | 'Conditional';
  occasion: string;
  date: Date | null;
  milestone: string;
  milestoneEstimateDate: Date | null;
  deliverySchedule: 'One-Time' | 'Recurring';
  frequency: string;
  endDate: Date | null;
  numberOfYears: string;
  additionalComments: string;
  ssn: string;
  primaryLegacyContact: any;
  recipientSnapshot: any;
  
  // Step 2: Messages
  messageType: 'Digital' | 'Physical' | null;
  messages: Array<{
    year: number;
    text: string;
    audioUrl: string;
  }>;
  
  // Step 3: Gift Selection - FIXED: itemRef can be null
  giftSection: {
    type: string;
    itemRef: string | null; // FIXED: Allow null to prevent ObjectId validation errors
    monetary?: {
      title: string;
      amount: number;
    };
    giftCard?: {
      title: string;
      denominations: number[];
      selectedAmount: number;
      sendMethod: string;
    };
    physicalItem?: {
      title: string;
      description: string;
      imageUrl: string;
      price: number;
    };
  };
  
  // Step 4: Review Data
  reviewData: any;
  
  // Step 5: Delivery & Payment
  deliveryData: {
    preferredDeliveryDate: Date | null;
    instructions: string;
    paymentMethod: string;
  };
  
  // Meta data
  status: 'Draft' | 'Scheduled' | 'Delivered';
  giftId: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  giftsteps?: string;
}

interface CreateGiftRequest {
  title: string;
  recipientId: string;
  type: 'Regular' | 'Post-Mortem';
  occasionType: 'Fixed' | 'Conditional';
  deliverySchedule: 'One-Time' | 'Recurring';
  
  // Fixed occasion fields
  occasion?: string;
  date?: string;
  
  // Conditional occasion fields
  milestone?: string;
  milestoneEstimateDate?: string;
  
  // Recurring fields - FIXED: Use nested recurringDetails object
  recurringDetails?: {
    frequency: string;
    numberOfYears: number;
  };
  
  // Legacy flat fields (for backward compatibility during transition)
  frequency?: string;
  endDate?: string;
  numberOfYears?: number;
  
  // Post-mortem specific
  ssn?: string;
  primaryLegacyContact?: {
    name: string;
    email: string;
    phoneNumber: string;
    alternatePhoneNumber?: string;
    relationship: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
  };
  
  additionalComments?: string;
}

// FIXED: Updated interface for draft save request with proper ObjectId handling
interface SaveDraftRequest extends CreateGiftRequest {
  giftId?: string;
  status?: 'Draft';
  messageType?: 'Digital' | 'Physical';
  messages?: Array<{
    year: number;
    text: string;
    audioUrl: string;
  }>;
  giftSection?: {
    type: string;
    itemRef: string | null; // FIXED: Allow null for ObjectId validation
    monetary?: any;
    giftCard?: any;
    physicalItem?: any;
  };
}

interface GiftFlowContextType {
  // Gift data
  currentGift: GiftData | null;
  setCurrentGift: React.Dispatch<React.SetStateAction<GiftData | null>>;
  updateGiftData: (updates: Partial<GiftData>) => void;
  resetGift: () => void;

  // Navigation
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  goToNextTab: () => void;
  goToPreviousTab: () => void;
  goToTab: (tabId: string) => void;

  // API functions
  createGift: (giftData: CreateGiftRequest) => Promise<GiftData | null>;
  saveGiftAsDraft: (giftData?: Partial<SaveDraftRequest>) => Promise<void>;
  loadGift: (giftId: string, setInitialTab?: boolean) => Promise<void>;

  // Validation
  validateAndProceed: (currentTabData: any) => boolean;
  isStepAccessible: (stepId: string) => boolean;
  validateStep1: () => boolean;
  validateStep2: () => boolean;
  validateStep3: () => boolean;
  validateStep4: () => boolean;
  revalidateAllSteps: () => void;
  isGiftSkipped: () => boolean;

  // Loading states
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadingGift: boolean;
  isSaving: boolean;
  isCreating: boolean;

  // Step completion tracking
  completedSteps: string[];
  markStepCompleted: (stepId: string) => void;
  invalidateStepsAfter: (stepId: string) => void;
}

const GiftFlowContext = createContext<GiftFlowContextType | undefined>(undefined);

export const useGiftContext = () => {
  const context = useContext(GiftFlowContext);
  if (!context) {
    throw new Error('useGiftContext must be used within a GiftFlowProvider');
  }
  return context;
};

// Export useGiftFlow as an alias for backward compatibility
export const useGiftFlow = useGiftContext;

interface GiftFlowProviderProps {
  children: React.ReactNode;
}

export const GiftFlowProvider: React.FC<GiftFlowProviderProps> = ({ children }) => {
  const [activeTab, setActiveTab] = useState<string>('1');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingGift, setIsLoadingGift] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [currentGift, setCurrentGift] = useState<GiftData | null>(null);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const tabOrder = ['1', '3', '4', '5', '6']; // Skip tab '2' (Occasion) as it's integrated in tab '1'

  const updateGiftData = (updates: Partial<GiftData>) => {
    // console.log('🔄 STEP NAVIGATION: updateGiftData called:', {
    //   hasFrequency: 'frequency' in updates,
    //   hasNumberOfYears: 'numberOfYears' in updates,
    //   frequencyValue: updates.frequency,
    //   numberOfYearsValue: updates.numberOfYears,
    //   deliverySchedule: updates.deliverySchedule,
    //   allUpdateKeys: Object.keys(updates)
    // });

    setCurrentGift(prev => {
      if (!prev) return null;

      // console.log('📋 STEP NAVIGATION: Before updateGiftData merge:', {
      //   prevFrequency: prev.frequency,
      //   prevNumberOfYears: prev.numberOfYears,
      //   prevDeliverySchedule: prev.deliverySchedule
      // });

      // CRITICAL FIX: Don't overwrite frequency/numberOfYears with empty values unless explicitly set
      const safeUpdates = { ...updates };

      // If frequency is being updated with an empty value but current gift has a value, preserve it
      if ('frequency' in updates && (!updates.frequency || updates.frequency === '') && prev.frequency) {
        // console.log('⚠️  STEP NAVIGATION: Preserving existing frequency:', prev.frequency);
        safeUpdates.frequency = prev.frequency;
      }

      // If numberOfYears is being updated with an empty value but current gift has a value, preserve it
      if ('numberOfYears' in updates && (!updates.numberOfYears || updates.numberOfYears === '') && prev.numberOfYears) {
        // console.log('⚠️  STEP NAVIGATION: Preserving existing numberOfYears:', prev.numberOfYears);
        safeUpdates.numberOfYears = prev.numberOfYears;
      }

      // CRITICAL: Check if recurring settings have changed (only if values are actually different)
      const recurringSettingsChanged =
        (safeUpdates.deliverySchedule && safeUpdates.deliverySchedule !== prev.deliverySchedule) ||
        (safeUpdates.frequency && safeUpdates.frequency !== prev.frequency) ||
        (safeUpdates.numberOfYears && safeUpdates.numberOfYears !== prev.numberOfYears);


      const updated = { ...prev, ...safeUpdates };

      // If recurring settings changed, invalidate all steps after step 1
      if (recurringSettingsChanged) {
        // console.log('🚨 STEP NAVIGATION: Recurring settings changed - invalidating subsequent steps');
        // Use setTimeout to ensure state is updated first
        setTimeout(() => {
          invalidateStepsAfter('1');
          // CHECKMARK FIX: Trigger revalidation after a short delay to allow components to adjust
          setTimeout(() => {
            // console.log('🔄 CHECKMARK FIX: Triggering revalidation after recurring settings change');
            // Note: revalidateAllSteps will be called automatically by the useEffect when currentGift changes
            // This setTimeout gives components time to adjust their local state before revalidation
          }, 500); // Give components time to adjust their message counts
        }, 0);
      }

      // console.log('✅ STEP NAVIGATION: After updateGiftData merge:', {
      //   updatedFrequency: updated.frequency,
      //   updatedNumberOfYears: updated.numberOfYears,
      //   updatedDeliverySchedule: updated.deliverySchedule
      // });

      return updated;
    });
  };

  // Reset gift context for a fresh start
  const resetGift = useCallback(() => {
    setCurrentGift(null);
    setActiveTab('1');
    setCompletedSteps([]);
    setIsLoading(false);
    setIsLoadingGift(false);
  }, []);

  const goToNextTab = () => {
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex < tabOrder.length - 1) {
      let nextTab = tabOrder[currentIndex + 1];
      
      // Skip Delivery & Payment (tab '6') if no gift is selected (Gift step was skipped)
      if (nextTab === '6' && (!currentGift?.giftSection || !currentGift.giftSection.type)) {
        // console.log('Skipping Delivery & Payment step - no gift selected');
        // If we're going from Review (5) to Delivery & Payment (6) but gift was skipped,
        // we should handle this differently in the Review component instead
        nextTab = tabOrder[currentIndex + 1]; // Keep the original logic, let Review handle it
      }
      
      // console.log('goToNextTab: going from', activeTab, 'to', nextTab);
      setActiveTab(nextTab);
    }
  };

  const goToPreviousTab = () => {
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex > 0) {
      const prevTab = tabOrder[currentIndex - 1];
      // console.log('goToPreviousTab: going from', activeTab, 'to', prevTab);
      setActiveTab(prevTab);
    }
  };

  const goToTab = (tabId: string) => {
    // console.log('goToTab called with:', tabId);
    // Set a flag to indicate this is internal navigation
    window.dispatchEvent(new CustomEvent('internalGiftNavigation', { 
      detail: { fromTab: activeTab, toTab: tabId } 
    }));
    setActiveTab(tabId);
  };

  // Dynamic validation functions for each step
  const validateStep1 = useCallback((): boolean => {

    // NOTE: don't require a persisted _id for client-side validation. Users may be
    // creating a new draft that hasn't been persisted yet (temp id in URL).
    // Requiring _id or deliverySchedule here caused Step1 to be treated as
    // incomplete and blocked subsequent steps/navigation when the rest of the
    // required fields were present. Only require the absolute basics (recipient/type).
    const hasBasicInfo = !!(currentGift?.recipientId && currentGift?.type);

    if (!hasBasicInfo) {
      return false;
    }

    // Check occasion-specific fields based on occasion type
    if (currentGift.occasionType === 'Fixed') {
      const isValid = !!(currentGift.occasion && currentGift.date);
      return isValid;
    } else if (currentGift.occasionType === 'Conditional') {
      const isValid = !!(currentGift.milestone && currentGift.milestoneEstimateDate);
      return isValid;
    }

    // Default case for backward compatibility
    const isValid = !!(currentGift.occasion && currentGift.date);
    return isValid;
  }, [currentGift]);

  const validateStep2 = useCallback((): boolean => {
    if (!currentGift?.messages || currentGift.messages.length === 0) {
      return false;
    }

    // Calculate expected number of messages based on delivery schedule and frequency
    let expectedMessageCount = 1;
    if (currentGift.deliverySchedule === 'Recurring') {
      const numberOfYears = parseInt(currentGift.numberOfYears) || 1;
      const frequency = currentGift.frequency;
      
      // For ALL recurring types: 1 message per year (numberOfYears = numberOfMessages)
      // - Annual: Different message each year
      // - Monthly: Same message monthly, but different message each year  
      // - Weekly: Same message weekly, but different message each year
      expectedMessageCount = numberOfYears;
    }


    // Check if we have the correct number of messages
    if (currentGift.messages.length !== expectedMessageCount) {
      return false;
    }

    // Check if all messages have valid content
    const allMessagesValid = currentGift.messages.every(msg => msg.text?.trim() && msg.audioUrl);
    
    return allMessagesValid;
  }, [currentGift]);

  const validateStep3 = useCallback((): boolean => {
    return !!(currentGift?.giftSection && 
              (currentGift.giftSection.monetary || 
               currentGift.giftSection.giftCard || 
               currentGift.giftSection.physicalItem));
  }, [currentGift]);

  const validateStep4 = useCallback((): boolean => {
    // Review step is accessible if all previous steps are valid
    return validateStep1() && validateStep2() && validateStep3();
  }, [validateStep1, validateStep2, validateStep3]);

  // Step completion tracking functions
  const markStepCompleted = useCallback((stepId: string) => {
    setCompletedSteps(prev => {
      if (!prev.includes(stepId)) {
        return [...prev, stepId];
      }
      return prev;
    });
  }, []);

  // Invalidate steps after a certain step when critical data changes
  const invalidateStepsAfter = useCallback((stepId: string) => {
    const stepOrder = ['1', '3', '4', '5', '6']; // Skip '2' (Occasion)
    const stepIndex = stepOrder.indexOf(stepId);
    
    if (stepIndex !== -1) {
      const stepsToRemove = stepOrder.slice(stepIndex + 1);
      
      setCompletedSteps(prev => {
        const updated = prev.filter(step => !stepsToRemove.includes(step));
        return updated;
      });
      
      // Show warning to user
      toast.warn('Settings changed. Please complete the subsequent steps again to ensure accuracy.');
    }
  }, []);

  // Check if step is accessible based on completion and validation
  const isStepAccessible = useCallback((stepId: string): boolean => {
    const stepIndex = tabOrder.indexOf(stepId);
    if (stepIndex === 0) return true; // First step is always accessible
    
    // CRITICAL: Check if ALL previous steps are completed (not invalidated)
    for (let i = 0; i < stepIndex; i++) {
      const prevStepId = tabOrder[i];
      
      // Check if step is completed (not invalidated)
      if (!completedSteps.includes(prevStepId)) {
        return false;
      }
    }
    
    return true;
  }, [completedSteps]);

  // Check if gift step was skipped (no gift selected)
  const isGiftSkipped = useCallback((): boolean => {
    return !currentGift?.giftSection || !currentGift.giftSection.type;
  }, [currentGift]);


  // Revalidate all steps and update completed steps based on actual data
  const revalidateAllSteps = useCallback(() => {
    const newCompletedSteps: string[] = [];
    
    if (validateStep1()) newCompletedSteps.push('1');
    if (validateStep2()) newCompletedSteps.push('3');
    if (validateStep3()) newCompletedSteps.push('4');
    if (validateStep4()) newCompletedSteps.push('5');
    
    
    setCompletedSteps(newCompletedSteps);
  }, [validateStep1, validateStep2, validateStep3, validateStep4, isGiftSkipped]);

  // Revalidate steps whenever gift data changes
  useEffect(() => {
    if (currentGift) {
      revalidateAllSteps();
    }
  }, [currentGift, revalidateAllSteps]);

  // Get validation function for a step
  const getStepValidator = useCallback((stepId: string) => {
    switch (stepId) {
      case '1': return validateStep1;
      case '3': return validateStep2;
      case '4': return validateStep3;
      case '5': return validateStep4;
      default: return () => true;
    }
  }, [validateStep1, validateStep2, validateStep3, validateStep4]);

  const createGift = useCallback(async (giftData: CreateGiftRequest): Promise<GiftData | null> => {
    try {
      setIsCreating(true);
      
      const authToken = localStorage.getItem('authToken');
      
      
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/gift/create-gift`,
        giftData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      

      if (response.data.status === 'Success') {
        const newGift = response.data.data;
        
        // Transform backend response to match our interface
        // CRITICAL FIX: Preserve milestone and milestoneEstimateDate from request if backend doesn't return them
        const transformedGift: GiftData = {
          _id: newGift._id,
          title: newGift.title,
          recipientId: newGift.recipientId,
          type: newGift.type,
          occasionType: newGift.occasionType,
          occasion: newGift.occasion || giftData.occasion || '',
          date: newGift.date ? new Date(newGift.date) : (giftData.date ? new Date(giftData.date) : null),
          milestone: newGift.milestone || giftData.milestone || '',
          milestoneEstimateDate: newGift.milestoneEstimateDate ? new Date(newGift.milestoneEstimateDate) : (giftData.milestoneEstimateDate ? new Date(giftData.milestoneEstimateDate) : null),
          // Default deliverySchedule to request value or One-Time when backend omits it
          deliverySchedule: newGift.deliverySchedule || giftData.deliverySchedule || 'One-Time',
          frequency: newGift.frequency || newGift.recurringDetails?.frequency || giftData.frequency || '',
          endDate: newGift.endDate ? new Date(newGift.endDate) : null,
          numberOfYears: newGift.numberOfYears?.toString() || newGift.recurringDetails?.numberOfYears?.toString() || giftData.numberOfYears?.toString() || '',
          additionalComments: newGift.additionalComments || '',
          ssn: newGift.ssn || '',
          primaryLegacyContact: newGift.primaryLegacyContact,
          recipientSnapshot: newGift.recipientSnapshot,
          messageType: newGift.messageType || 'Digital',
          messages: newGift.messages || [],
          giftSection: newGift.giftSection || {
            type: '',
            itemRef: null, // FIXED: Initialize as null instead of empty string
          },
          reviewData: {},
          deliveryData: {
            preferredDeliveryDate: null,
            instructions: '',
            paymentMethod: ''
          },
          status: newGift.status || 'Draft',
          giftId: newGift._id,
          createdAt: newGift.createdAt ? new Date(newGift.createdAt) : new Date(),
          updatedAt: newGift.updatedAt ? new Date(newGift.updatedAt) : new Date(),
          giftsteps: newGift.giftsteps
        };


        setCurrentGift(transformedGift);
          // Defensive immediate mark to ensure UI reflects completion promptly.
          markStepCompleted('1'); // Mark step 1 (Start a Gift) as completed
        toast.success(response.data.message || 'Gift created successfully!');
        return transformedGift;
      } else {
        toast.error(response.data.message || 'Failed to create gift');
        return null;
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create gift. Please try again.');
      return null;
    } finally {
      setIsCreating(false);
    }
  }, [setIsCreating, setCurrentGift, markStepCompleted]);

  // FIXED: Complete rewrite of saveGiftAsDraft with proper ObjectId handling
  const saveGiftAsDraft = useCallback(async (additionalData?: Partial<SaveDraftRequest>) => {
    try {
      setIsSaving(true);
      
      const authToken = localStorage.getItem('authToken');
      
      // Prepare the draft data
      
      // Helper function to handle cleared fields (empty string should become null)
      const getFieldValue = (additionalValue: any, currentValue: any, defaultValue?: any) => {
        // If additionalData explicitly provides a value (even empty string), use it
        if (additionalData && additionalValue !== undefined) {
          return additionalValue === '' ? (defaultValue !== undefined ? defaultValue : null) : additionalValue;
        }
        // Otherwise fall back to current value
        return currentValue !== undefined && currentValue !== '' ? currentValue : defaultValue;
      };

      const occasionTypeValue = getFieldValue(additionalData?.occasionType, currentGift?.occasionType, 'Fixed');

      const draftData: SaveDraftRequest = {
        title: getFieldValue(additionalData?.title, currentGift?.title, 'Untitled Gift'),
        type: getFieldValue(additionalData?.type, currentGift?.type, 'Regular'),
        occasionType: occasionTypeValue,
        // Only include deliverySchedule when occasionType is not 'Conditional'
        ...(occasionTypeValue !== 'Conditional' && {
          deliverySchedule: getFieldValue(additionalData?.deliverySchedule, currentGift?.deliverySchedule, 'One-Time')
        }),
        status: 'Draft',
        // Override recipientId to be null if it's empty string
        recipientId: additionalData?.recipientId !== undefined
          ? (additionalData.recipientId === '' ? null : additionalData.recipientId)
          : (currentGift?.recipientId && currentGift.recipientId !== '' ? currentGift.recipientId : null)
      };

      // Add existing gift ID if updating
      if (currentGift?._id) {
        draftData.giftId = currentGift._id;
      }

      // Add messageType if provided
      if (additionalData?.messageType || currentGift?.messageType) {
        draftData.messageType = additionalData?.messageType || currentGift?.messageType;
      }

      // Add occasion data based on type
      if (currentGift?.occasionType === 'Fixed' || additionalData?.occasionType === 'Fixed') {
        draftData.occasion = getFieldValue(additionalData?.occasion, currentGift?.occasion, null);

        // Handle date field - if explicitly provided in additionalData, use it; otherwise fall back
        if (additionalData && 'date' in additionalData) {
          draftData.date = additionalData.date || null;
        } else if (currentGift?.date) {
          draftData.date = currentGift.date instanceof Date ? currentGift.date.toISOString() : currentGift.date;
        }
        
        if (draftData.deliverySchedule === 'Recurring') {
          // FIXED: Use nested recurringDetails structure with proper null handling
          const frequency = getFieldValue(
            additionalData?.frequency || additionalData?.recurringDetails?.frequency,
            currentGift?.frequency,
            null
          );

          const numberOfYearsValue = additionalData?.numberOfYears || additionalData?.recurringDetails?.numberOfYears;
          const numberOfYears = numberOfYearsValue !== undefined
            ? (numberOfYearsValue === '' ? null : (typeof numberOfYearsValue === 'string' ? parseInt(numberOfYearsValue) : numberOfYearsValue))
            : (currentGift?.numberOfYears ? parseInt(currentGift.numberOfYears) : null);

          if (frequency && numberOfYears) {
            draftData.recurringDetails = {
              frequency: frequency,
              numberOfYears: numberOfYears
            };
          }

          // Also set legacy flat fields for backward compatibility
          draftData.frequency = frequency;
          draftData.numberOfYears = numberOfYears;
          
          if (currentGift.endDate) {
            draftData.endDate = currentGift.endDate.toISOString();
          }
          
        }
      } else if (currentGift?.occasionType === 'Conditional' || additionalData?.occasionType === 'Conditional') {
        draftData.milestone = getFieldValue(additionalData?.milestone, currentGift?.milestone, null);

        // Handle milestoneEstimateDate - if explicitly provided in additionalData, use it; otherwise fall back
        if (additionalData && 'milestoneEstimateDate' in additionalData) {
          draftData.milestoneEstimateDate = additionalData.milestoneEstimateDate || null;
        } else if (currentGift?.milestoneEstimateDate) {
          draftData.milestoneEstimateDate = currentGift.milestoneEstimateDate.toISOString();
        }
      }

      // Add post-mortem specific data
      if (draftData.type === 'Post-Mortem') {
        draftData.ssn = getFieldValue(additionalData?.ssn, currentGift?.ssn, null);
        draftData.primaryLegacyContact = getFieldValue(additionalData?.primaryLegacyContact, currentGift?.primaryLegacyContact, null);

        if (draftData.deliverySchedule === 'Recurring') {
          // FIXED: For Post-Mortem, ensure recurring fields are properly set with null handling
          const frequency = getFieldValue(
            additionalData?.frequency || additionalData?.recurringDetails?.frequency,
            currentGift?.frequency,
            null
          );

          const numberOfYearsValue = additionalData?.numberOfYears || additionalData?.recurringDetails?.numberOfYears;
          const numberOfYears = numberOfYearsValue !== undefined
            ? (numberOfYearsValue === '' ? null : (typeof numberOfYearsValue === 'string' ? parseInt(numberOfYearsValue) : numberOfYearsValue))
            : (currentGift?.numberOfYears ? parseInt(currentGift.numberOfYears) : null);

          if (frequency && numberOfYears) {
            draftData.recurringDetails = {
              frequency: frequency,
              numberOfYears: numberOfYears
            };
          }

          // Also set legacy flat fields for backward compatibility
          draftData.frequency = frequency;
          draftData.numberOfYears = numberOfYears;
        }
      }

      // Add messages - prioritize additionalData to handle cleared messages
      if (additionalData && 'messages' in additionalData) {
        // If messages are explicitly provided in additionalData (even if empty array), use them
        draftData.messages = additionalData.messages || [];
      } else if (currentGift?.messages && currentGift.messages.length > 0) {
        // Otherwise, fall back to current messages
        draftData.messages = currentGift.messages;
      }

      // FIXED: Handle gift section with proper ObjectId validation
      if (currentGift?.giftSection) {
        const { type, itemRef, monetary, giftCard, physicalItem } = currentGift.giftSection;
        
        // Only include giftSection if there's meaningful data
        const hasType = type && type.trim() !== '';
        const hasValidItemRef = itemRef && itemRef.trim() !== '';
        const hasGiftData = monetary || giftCard || physicalItem;
        
        if (hasType || hasValidItemRef || hasGiftData) {
          draftData.giftSection = {
            type: type || '',
            // CRITICAL FIX: Convert empty string to null for ObjectId validation
            itemRef: hasValidItemRef ? itemRef : null,
            ...(monetary && { monetary }),
            ...(giftCard && { giftCard }),
            ...(physicalItem && { physicalItem })
          };
        }
        // If no meaningful gift section data, don't include it in the payload
      }

      // Add additional comments - handle cleared values
      if (additionalData && 'additionalComments' in additionalData) {
        draftData.additionalComments = additionalData.additionalComments || '';
      } else if (currentGift?.additionalComments) {
        draftData.additionalComments = currentGift.additionalComments;
      }

      

      const response = await axios.post(
        `${apiConfig.apiBaseURL}/gift/save-gift-draft`,
        draftData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      

      if (response.data.status === 'Success') {
        const savedGift = response.data.data;
        
        if (savedGift) {
          // CRITICAL FIX: Preserve recurring settings from additionalData if backend doesn't return them
          const frequency = savedGift.frequency || 
                           savedGift.recurringDetails?.frequency || 
                           additionalData?.frequency ||
                           additionalData?.recurringDetails?.frequency ||
                           currentGift?.frequency || 
                           '';
                           
          const numberOfYears = savedGift.numberOfYears?.toString() || 
                               savedGift.recurringDetails?.numberOfYears?.toString() || 
                               additionalData?.numberOfYears?.toString() ||
                               additionalData?.recurringDetails?.numberOfYears?.toString() ||
                               currentGift?.numberOfYears || 
                               '';


          // CRITICAL FIX: Respect explicitly cleared fields from additionalData
          const transformedGift: GiftData = {
            _id: savedGift._id,
            title: savedGift.title,
            recipientId: savedGift.recipientId,
            type: savedGift.type,
            occasionType: savedGift.occasionType,
            // If additionalData explicitly provides occasion (even if empty), use it; otherwise use backend or fallback
            occasion: additionalData && 'occasion' in additionalData
              ? (additionalData.occasion || '')
              : (savedGift.occasion !== undefined ? savedGift.occasion : (currentGift?.occasion || '')),
            // If additionalData explicitly provides date (even if null), use it; otherwise use backend or fallback
            date: additionalData && 'date' in additionalData
              ? (additionalData.date ? new Date(additionalData.date) : null)
              : (savedGift.date ? new Date(savedGift.date) : (currentGift?.date || null)),
            // If additionalData explicitly provides milestone (even if empty), use it; otherwise use backend or fallback
            milestone: additionalData && 'milestone' in additionalData
              ? (additionalData.milestone || '')
              : (savedGift.milestone !== undefined ? savedGift.milestone : (currentGift?.milestone || '')),
            // If additionalData explicitly provides milestoneEstimateDate (even if null), use it; otherwise use backend or fallback
            milestoneEstimateDate: additionalData && 'milestoneEstimateDate' in additionalData
              ? (additionalData.milestoneEstimateDate ? new Date(additionalData.milestoneEstimateDate) : null)
              : (savedGift.milestoneEstimateDate ? new Date(savedGift.milestoneEstimateDate) : (currentGift?.milestoneEstimateDate || null)),
            // Default deliverySchedule to a known value if backend omitted it
            deliverySchedule: savedGift.deliverySchedule || additionalData?.deliverySchedule || currentGift?.deliverySchedule || 'One-Time',
            frequency: frequency,
            endDate: savedGift.endDate ? new Date(savedGift.endDate) : null,
            numberOfYears: numberOfYears,
            // If additionalData explicitly provides additionalComments (even if empty), use it
            additionalComments: additionalData && 'additionalComments' in additionalData
              ? (additionalData.additionalComments || '')
              : (savedGift.additionalComments || ''),
            // If additionalData explicitly provides ssn (even if empty), use it
            ssn: additionalData && 'ssn' in additionalData
              ? (additionalData.ssn || '')
              : (savedGift.ssn || ''),
            // If additionalData explicitly provides primaryLegacyContact, use it
            primaryLegacyContact: additionalData && 'primaryLegacyContact' in additionalData
              ? additionalData.primaryLegacyContact
              : savedGift.primaryLegacyContact,
            recipientSnapshot: savedGift.recipientSnapshot,
            messageType: savedGift.messageType || currentGift?.messageType || 'Digital',
            // CRITICAL FIX: Prioritize additionalData.messages to handle cleared messages
            // If messages were explicitly sent in additionalData, use them (even if empty array)
            messages: additionalData && 'messages' in additionalData
              ? (additionalData.messages ?? [])
              : (savedGift.messages !== undefined ? savedGift.messages : (currentGift?.messages || [])),
            giftSection: savedGift.giftSection || currentGift?.giftSection || {
              type: '',
              itemRef: null, // FIXED: Default to null
            },
            reviewData: {},
            deliveryData: {
              preferredDeliveryDate: null,
              instructions: '',
              paymentMethod: ''
            },
            status: 'Draft',
            giftId: savedGift._id,
            createdAt: savedGift.createdAt ? new Date(savedGift.createdAt) : new Date(),
            updatedAt: savedGift.updatedAt ? new Date(savedGift.updatedAt) : new Date(),
            giftsteps: savedGift.giftsteps
          };
          
          setCurrentGift(transformedGift);
          // Ensure step 1 marked for drafts as well (defensive)
          markStepCompleted('1');
        }
        
        toast.success('Draft saved successfully!');
      } else {
        throw new Error(response.data.message || 'Failed to save draft');
      }
    } catch (error: any) {
      
      // Better error handling with specific ObjectId error
      let errorMessage = 'Failed to save draft. Please try again.';
      
      if (error.response?.data?.error && error.response.data.error.includes('Cast to ObjectId failed')) {
        errorMessage = 'Invalid gift item selection. Please select a valid gift item or leave it empty.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [setIsSaving, setCurrentGift, currentGift]);

  const loadGift = useCallback(async (giftId: string, setInitialTab: boolean = true) => {
    try {
      setIsLoadingGift(true);
      
      const authToken = localStorage.getItem('authToken');
      
      
      const response = await axios.get(
        `${apiConfig.apiBaseURL}/gift/get-single-gift/${giftId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      

      if (response.data.status === 'Success') {
        const loadedGift = response.data.data;
        
        const transformedGift: GiftData = {
          _id: loadedGift._id,
          title: loadedGift.title,
          recipientId: loadedGift.recipientId,
          type: loadedGift.type,
          occasionType: loadedGift.occasionType,
          occasion: loadedGift.occasion || '',
          date: loadedGift.date ? new Date(loadedGift.date) : null,
          milestone: loadedGift.milestone || '',
          milestoneEstimateDate: loadedGift.milestoneEstimateDate ? new Date(loadedGift.milestoneEstimateDate) : null,
          // Default deliverySchedule to a known value if backend omitted it
          deliverySchedule: loadedGift.deliverySchedule || 'One-Time',
          frequency: loadedGift.frequency || loadedGift.recurringDetails?.frequency || '',
          endDate: loadedGift.endDate ? new Date(loadedGift.endDate) : null,
          numberOfYears: loadedGift.numberOfYears?.toString() || loadedGift.recurringDetails?.numberOfYears?.toString() || '',
          additionalComments: loadedGift.additionalComments || '',
          ssn: loadedGift.ssn || '',
          primaryLegacyContact: loadedGift.primaryLegacyContact,
          recipientSnapshot: loadedGift.recipientSnapshot,
          messageType: loadedGift.messageType || 'Digital',
          messages: loadedGift.messages || [],
          giftSection: loadedGift.giftSection || {
            type: '',
            itemRef: null, // FIXED: Default to null
          },
          reviewData: {},
          deliveryData: {
            preferredDeliveryDate: null,
            instructions: '',
            paymentMethod: ''
          },
          status: loadedGift.status || 'Draft',
          giftId: loadedGift._id,
          createdAt: loadedGift.createdAt ? new Date(loadedGift.createdAt) : new Date(),
          updatedAt: loadedGift.updatedAt ? new Date(loadedGift.updatedAt) : new Date(),
          giftsteps: loadedGift.giftsteps
        };
        
        // Check if gift is already paid/scheduled - redirect to success page
        if (loadedGift.status === 'Scheduled' || loadedGift.paymentStatus === 'Paid') {
          toast.info('This gift has already been completed and paid for.');
          // Set flag to prevent beforeunload warning when navigating to success page
          sessionStorage.setItem('giftCompletionInProgress', 'true');
          window.location.href = `/gift-success?giftId=${loadedGift._id}`;
          return;
        }
        
        setCurrentGift(transformedGift);
  // Defensive mark for loaded gifts
  markStepCompleted('1');
        
        // Mark steps as completed based on existing gift data
        if (loadedGift._id) {
          markStepCompleted('1'); // Gift exists, so step 1 is completed
        }
        if (loadedGift.messages && loadedGift.messages.length > 0) {
          markStepCompleted('3'); // Messages exist, so step 2 is completed
        }
        if (loadedGift.giftSection && loadedGift.giftSection.type) {
          markStepCompleted('4'); // Gift items exist, so step 3 is completed
        }
        
        // Set the active tab based on gift steps (only on initial load)
        if (setInitialTab && loadedGift.giftsteps) {
          switch (loadedGift.giftsteps) {
            case 'Step2':
              setActiveTab('3'); // Message tab
              break;
            case 'Step3':
              setActiveTab('4'); // Gift tab
              break;
            case 'Step4':
              setActiveTab('5'); // Review tab
              break;
            case 'Step5':
              setActiveTab('6'); // Delivery tab
              break;
            default:
              setActiveTab('1'); // Start tab
          }
        }
      } else {
        toast.error(response.data.message || 'Failed to load gift');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load gift. Please try again.');
    } finally {
      setIsLoadingGift(false);
    }
  }, [setIsLoadingGift, setCurrentGift, setActiveTab, markStepCompleted]);


  const validateAndProceed = (currentTabData: any): boolean => {
    // Update gift data with current tab data
    updateGiftData(currentTabData);

    // Validation logic for each tab
    switch (activeTab) {
      case '1': // Gift Information
        // Validate common required fields
        if (!currentTabData.title || !currentTabData.recipientId) {
          return false;
        }

        // Occasion-specific validation
        if (currentTabData.occasionType === 'Fixed') {
          if (!currentTabData.occasion || !currentTabData.date) return false;
        } else if (currentTabData.occasionType === 'Conditional') {
          // Conditional occasions require milestone + estimate date
          if (!currentTabData.milestone || !currentTabData.milestoneEstimateDate) return false;
        } else {
          // Backwards-compatible fallback
          if (!currentTabData.occasion || !currentTabData.date) return false;
        }

        if (currentTabData.type === 'Post-Mortem' && !currentTabData.ssn) {
          return false;
        }

        if (currentTabData.deliverySchedule === 'Recurring') {
          if (currentTabData.type === 'Regular' && !currentTabData.endDate) {
            return false;
          }
          if (currentTabData.type === 'Post-Mortem' && !currentTabData.numberOfYears) {
            return false;
          }
        }
        break;
        
      case '3': // Message
        if (!currentTabData.messages || currentTabData.messages.length === 0) {
          return false;
        }
        // Validate each message has both text and audio
        for (const message of currentTabData.messages) {
          if (!message.text || !message.audioUrl) {
            return false;
          }
        }
        break;
        
      case '4': // Gift
        if (!currentTabData.giftSection || !currentTabData.giftSection.type) {
          return false;
        }
        break;
        
      case '5': // Review
        // Review is mostly display, minimal validation
        break;
        
      case '6': // Delivery & Payment
        if (!currentTabData.deliveryData || !currentTabData.deliveryData.paymentMethod) {
          return false;
        }
        break;
        
      default:
        break;
    }
    
    // If validation passed, mark this step as completed immediately so UI reflects it
    try {
      const stepIdsThatMap = ['1', '3', '4', '5'];
      if (stepIdsThatMap.includes(activeTab)) {
        markStepCompleted(activeTab);
      }
    } catch (e) {
      // swallow any errors here - non-critical
    }

    return true;
  };

  const contextValue: GiftFlowContextType = {
    currentGift,
    setCurrentGift,
    updateGiftData,
    resetGift,
    activeTab,
    setActiveTab,
    goToNextTab,
    goToPreviousTab,
    goToTab,
    createGift,
    saveGiftAsDraft,
    loadGift,
    validateAndProceed,
    isStepAccessible,
    validateStep1,
    validateStep2,
    validateStep3,
    validateStep4,
    revalidateAllSteps,
    isGiftSkipped,
    isLoading,
    setIsLoading,
    isLoadingGift,
    isSaving,
    isCreating,
    completedSteps,
    markStepCompleted,
    invalidateStepsAfter
  };

  return (
    <GiftFlowContext.Provider value={contextValue}>
      {children}
    </GiftFlowContext.Provider>
  );
};

export { GiftFlowContext };
export default GiftFlowProvider;